import { Link } from "react-router-dom";
import { useForgotPasswordViewModel } from "../../viewmodel/useForgotPasswordViewModel";
import { useInlineLoading } from "@/shared/loading";
import { routes } from "@/app/navigation/routes";
import andyHdLogo from "@/assets/andyhdlogo.png";
import farmhouseBg from "@/assets/images/login-farmhouse-bg.jpg";
import { forgotPasswordImage } from "@/assets/images";
import styles from "./ForgotPasswordForm.module.css";

/**
 * ForgotPasswordForm Component
 * Matches exact UI mockup with centered modern card, farmhouse villa backdrop,
 * direction signpost graphic, pill inputs, and bottom-left copyright footer.
 * Preserves all authentication logic and bindings intact.
 */
export const ForgotPasswordForm = () => {
  const { email, isLoading, emailError, updateEmail, handleSubmit } = useForgotPasswordViewModel();
  const BUTTON_ID = "forgot-password-button";
  const { isLoading: isInlineLoading } = useInlineLoading(BUTTON_ID);
  const buttonLoading = isLoading || isInlineLoading;

  return (
    <div className={styles.pageContainer} style={{ backgroundImage: `url(${farmhouseBg})` }}>
      <div className={styles.pageOverlay} />

      {/* Top Left Header / Logo */}
      <header className={styles.header}>
        <Link to={routes.landing} className={styles.logoWrap}>
          <img src={andyHdLogo} alt="Andy Mascot" className={styles.logoImg} />
          <span className={styles.logoText}>ANDY</span>
        </Link>
      </header>

      {/* Main Content: Centered Floating Card */}
      <main className={styles.mainContent}>
        <div className={styles.cardWrapper}>
          <div className={styles.card}>
            <img
              src={forgotPasswordImage}
              alt="Forgot Password"
              className={styles.forgotPasswordImage}
            />
            <h1 className={styles.title}>Forgot Password</h1>
            <p className={styles.subtitle}>
              Enter your email address and we&apos;ll send you a link to reset your password.
            </p>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="email">
                  Email
                </label>
                <div className={`${styles.inputBox} ${emailError ? styles.inputBoxError : ""}`}>
                  <span className={styles.inputIcon}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect width="20" height="16" x="2" y="4" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                  </span>
                  <input
                    id="email"
                    type="email"
                    className={styles.input}
                    value={email}
                    onChange={(e) => updateEmail(e.target.value)}
                    placeholder="[EMAIL_ADDRESS]"
                  />
                </div>
                {emailError && <div className={styles.errorMessage}>{emailError}</div>}
              </div>

              <button
                id={BUTTON_ID}
                type="submit"
                className={styles.continueButton}
                disabled={buttonLoading}
              >
                {buttonLoading ? "Loading..." : "Send Reset Link"}
              </button>
            </form>

            {/* Back to Login Link */}
            <div className={styles.backToLoginLink}>
              <Link to={routes.login} className={styles.backToLoginLinkText}>
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer at Bottom Left */}
      <footer className={styles.footer}>
        <span>Copyright©2026</span>
        <span className={styles.footerDivider}>|</span>
        <Link to="#" className={styles.footerLink}>
          Privacy Policy
        </Link>
      </footer>
    </div>
  );
};
